import React, {
  forwardRef,
  FC,
  ReactNode,
  useLayoutEffect,
  useRef,
  ReactElement,
  HTMLAttributes,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Portal from '../../layout/Portal/Portal';
import TagWrapper from '../TagWrapper';

// ---------- Modal Title ----------
interface IModalTitleProps extends HTMLAttributes<HTMLElement> {
  id: string;
  children: ReactNode;
  className?: string;
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';
}
export const ModalTitle = forwardRef<HTMLHeadingElement, IModalTitleProps>(
  ({ tag, id, children, className, ...props }, ref) => {
    return (
      <TagWrapper
        tag={tag}
        ref={ref}
        id={id}
        className={classNames('modal-title', className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);
ModalTitle.displayName = 'ModalTitle';
ModalTitle.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  tag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span']),
};
ModalTitle.defaultProps = { className: undefined, tag: 'h5' };

// ---------- Modal Header ----------
interface IModalHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactElement<IModalTitleProps> | ReactNode;
  className?: string;
  setIsOpen?(...args: unknown[]): unknown;
}
export const ModalHeader = forwardRef<HTMLDivElement, IModalHeaderProps>(
  ({ children, className, setIsOpen, ...props }, ref) => (
    <div ref={ref} className={classNames('modal-header', className)} {...props}>
      {children}
      {setIsOpen && (
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
);
ModalHeader.displayName = 'ModalHeader';
ModalHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  setIsOpen: PropTypes.func,
};
ModalHeader.defaultProps = { className: undefined, setIsOpen: undefined };

// ---------- Modal Body ----------
interface IModalBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
}
export const ModalBody = forwardRef<HTMLDivElement, IModalBodyProps>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={classNames('modal-body', className)} {...props}>
      {children}
    </div>
  )
);
ModalBody.displayName = 'ModalBody';
ModalBody.propTypes = { children: PropTypes.node.isRequired, className: PropTypes.string };
ModalBody.defaultProps = { className: undefined };

// ---------- Modal Footer ----------
interface IModalFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
}
export const ModalFooter = forwardRef<HTMLDivElement, IModalFooterProps>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={classNames('modal-footer', className)} {...props}>
      {children}
    </div>
  )
);
ModalFooter.displayName = 'ModalFooter';
ModalFooter.propTypes = { children: PropTypes.node.isRequired, className: PropTypes.string };
ModalFooter.defaultProps = { className: undefined };

// ---------- Modal ----------
interface IModalProps {
  children: ReactElement | ReactNode;
  isOpen: boolean;
  setIsOpen(...args: unknown[]): unknown;
  id?: string;
  titleId?: string;
  isStaticBackdrop?: boolean;
  isScrollable?: boolean;
  isCentered?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | null;
  fullScreen?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | boolean;
  isAnimation?: boolean;
  classNameMd?: string;
}
const Modal: FC<IModalProps> = ({
  children,
  isOpen,
  setIsOpen,
  id,
  titleId,
  isStaticBackdrop,
  isScrollable,
  isCentered,
  size,
  fullScreen,
  isAnimation,
  classNameMd,
  ...props
}) => {
  const refModal = useRef<HTMLDivElement | null>(null);

  // Body class
  useLayoutEffect(() => {
    if (isOpen) document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [isOpen]);

  // Animation props
  const ANIMATION_PROPS = isAnimation
    ? {
        initial: { opacity: 0, y: '-50%' },
        animate: { opacity: 1, x: '0%', y: '0%' },
        exit: { opacity: 0, y: '-50%' },
        transition: { ease: 'easeInOut', duration: 0.3 },
      }
    : {};

  return (
    <Portal>
      <AnimatePresence exitBeforeEnter>
        {isOpen && (
          <>
            {/* Modal */}
            <motion.div
              ref={refModal}
              key="modal"
              className={classNames('modal', classNameMd, { fade: isAnimation }, 'show')}
              role="dialog"
              style={{ display: 'block' }}
              id={id}
              tabIndex={-1}
              aria-labelledby={titleId}
              data-bs-backdrop={isStaticBackdrop ? 'static' : undefined}
              data-bs-keyboard={isStaticBackdrop ? 'false' : undefined}
              {...ANIMATION_PROPS}
              {...props}
              onClick={() => !isStaticBackdrop && setIsOpen(false)} // click on backdrop
            >
              <div
                className={classNames('modal-dialog', {
                  'modal-dialog-scrollable': isScrollable,
                  'modal-dialog-centered': isCentered,
                  [`modal-${size}`]: size,
                  [`modal-fullscreen${typeof fullScreen === 'string' ? `-${fullScreen}-down` : ''}`]: fullScreen,
                }, 'modal-size')}
                onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
              >
                <div className="modal-content">{children}</div>
              </div>
            </motion.div>

            {/* Backdrop */}
            <div className={classNames('modal-backdrop', classNameMd, { fade: isAnimation }, 'show')} />
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};

import { Validator } from 'prop-types';

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  titleId: PropTypes.string,
  isStaticBackdrop: PropTypes.bool,
  isScrollable: PropTypes.bool,
  isCentered: PropTypes.bool,
  size: PropTypes.oneOf([null, 'sm', 'lg', 'xl']),
  fullScreen: (PropTypes.oneOf(['sm','md','lg','xl','xxl']).isRequired as unknown as Validator<
    'sm' | 'md' | 'lg' | 'xl' | 'xxl' | boolean | null | undefined
  >) || (PropTypes.bool as unknown as Validator<
    'sm' | 'md' | 'lg' | 'xl' | 'xxl' | boolean | null | undefined
  >),
  isAnimation: PropTypes.bool,
};

Modal.defaultProps = {
  id: undefined,
  isStaticBackdrop: false,
  isScrollable: false,
  isCentered: false,
  size: null,
  fullScreen: false,
  isAnimation: true,
  titleId: undefined,
};

export default Modal;
